import React, { useState, useEffect } from 'react';
import { 
  Container,
  Paper,
  Typography,
  TextField, 
  Button, 
  Box, 
  MenuItem, 
  Grid, 
  FormControl, 
  InputLabel, 
  Select,
  FormHelperText,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableRow,
  TableCell
} from '@mui/material';
import { addCustomer, getCustomerById, updateCustomer, lookupCompanyByVAT } from '../services/customerService';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { countries } from '../../../data/countries';
import { useTranslation } from "react-i18next";
import { toast } from 'react-toastify';

const AddCustomerPage = () => {
  // ✅ Get id from URL params to detect edit mode
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  
  // ✅ Check if we're in edit mode
  const isEditMode = Boolean(id);
  const navigationData = location.state?.customerData;

  const [VATNumber, setVATNumber] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [street, setStreet] = useState('');
  const [box, setBox] = useState('');
  const [postCode, setPostCode] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('Belgium');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [peppolId, setPeppolId] = useState('');
  const [isActive, setIsActive] = useState('true');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode && !navigationData); // ✅ Only show loading in edit mode
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState(''); // Server-level error message
  const [vatSearching, setVatSearching] = useState(false);
  const [companyDataDialog, setCompanyDataDialog] = useState(false);
  const [companyData, setCompanyData] = useState(null);

  // ✅ Load customer data when in edit mode
  useEffect(() => {
    if (isEditMode) {
      if (navigationData) {
        populateForm(navigationData);
      } else {
        loadCustomer();
      }
    }
    // eslint-disable-next-line
  }, [id, navigationData]);

  const loadCustomer = async () => {
    try {
      setInitialLoading(true);
      const response = await getCustomerById(id);
      const customerData = response?.data?.payload || response?.payload || response?.data || response;
      if (customerData) {
        populateForm(customerData);
      } else {
        throw new Error('Customer not found');
      }
    } catch (error) {
      toast.error(t("customer_not_found"));
      navigate('/customers');
    } finally {
      setInitialLoading(false);
    }
  };

  // Handle VAT Number Lookup
  const handleVATLookup = async () => {
    if (!VATNumber || VATNumber.trim() === '') {
      toast.error(t("please_enter_vat_number") || "Please enter a VAT number");
      return;
    }

    // Remove 'BE' prefix if present
    const cleanVAT = VATNumber.replace(/^BE/i, '').trim();
    
    if (!/^\d+$/.test(cleanVAT)) {
      toast.error(t("invalid_vat_format") || "Invalid VAT format. Only numbers are allowed");
      return;
    }

    try {
      setVatSearching(true);
      // Clear any previous VAT errors
      if (errors.VATNumber) {
        setErrors(prev => ({ ...prev, VATNumber: undefined }));
      }
      const response = await lookupCompanyByVAT(cleanVAT);
      const data = response?.payload || response?.data?.payload || response;
      
      if (data && data.Found) {
        setCompanyData(data);
        setCompanyDataDialog(true);
      } else {
        toast.warning(t("company_not_found") || "No company found with this VAT number");
      }
    } catch (error) {
      console.error('Error looking up VAT:', error);
      // Check if it's a validation error (duplicate VAT)
      const errorMessage = error.response?.data?.message || '';
      if (errorMessage) {
        // Show validation error under the VAT field
        setErrors(prev => ({ ...prev, VATNumber: errorMessage }));
      }
      // No toast messages - all errors shown inline under the field
    } finally {
      setVatSearching(false);
    }
  };

  // Handle company data confirmation
  const handleConfirmCompanyData = () => {
    if (companyData) {
      // Map company data to form fields
      setVATNumber(companyData.EnterpriseNumber || VATNumber);
      setCompanyName(companyData.CompanyName || '');
      setName(companyData.CompanyName || ''); // Map company name to customer name field
      setStreet(companyData.StreetAddress || '');
      setBox(companyData.Box || '');
      setPostCode(companyData.PostCode || '');
      setCity(companyData.City || '');
      
      // Extract country from FullAddress or default to Belgium
      if (companyData.FullAddress && companyData.FullAddress.includes('BE')) {
        setCountry('Belgium');
      }
      
      // Set status based on company status
      if (companyData.Status) {
        setIsActive(companyData.Status.toLowerCase() === 'active' ? 'true' : 'false');
      }
      
      // Email and Phone from API (might be empty)
      if (companyData.Email) setEmail(companyData.Email);
      if (companyData.Phone) setPhoneNumber(companyData.Phone);
      
      toast.success(t("company_data_loaded") || "Company data loaded successfully. Please fill remaining fields.");
      setCompanyDataDialog(false);
      setCompanyData(null);
    }
  };

  // ✅ Populate form with customer data
  const populateForm = (data) => {
    setVATNumber(data.VATNumber || '');
    setCompanyName(data.CompanyName || '');
    setName(data.Name || data.CustomerName || '');
    setEmail(data.Email || data.email || '');
    setStreet(data.Street || '');
    setBox(data.Box || '');
    setPostCode(data.PostCode || '');
    setCity(data.City || '');
    setCountry(data.Country || 'Belgium');
    setPhoneNumber(data.PhoneNumber || data.Phone || '');
    setPeppolId(data.CustomerPeppolId || data.PeppolId || '');
    setIsActive(data.Status !== false ? 'true' : 'false');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    setErrors({});
    setServerError('');
    
    try {
      // Format VAT number: remove lowercase 'be' if present, then add 'BE' prefix
      let formattedVATNumber = VATNumber.trim();
      if (formattedVATNumber.toLowerCase().startsWith('be')) {
        formattedVATNumber = formattedVATNumber.substring(2).trim();
      }
      formattedVATNumber = 'BE' + formattedVATNumber;

      const record = {
        VATNumber: formattedVATNumber.trim(),
        CompanyName: companyName.trim(),
        Name: name.trim(),
        Email: email.trim(),
        Street: street.trim(),
        Box: box ? box.trim() : '',
        PostCode: postCode.trim(),
        City: city.trim(),
        Country: country.trim(),
        PhoneNumber: phoneNumber.trim(),
        CustomerPeppolId: peppolId ? peppolId.trim() : '',
        Status: isActive === 'true',
        ...(isEditMode ? { Id: parseInt(id) } : { CreatedDate: new Date().toISOString(), IsDeleted: false })
      };

      // ✅ Validation - Only required fields
      const newErrors = {};
      
      if (!record.VATNumber || record.VATNumber.trim() === '') {
        newErrors.VATNumber = "This field is required";
      } else if (record.VATNumber.length > 20) {
        newErrors.VATNumber = "Maximum 20 characters allowed";
      }
      
      if (!record.CompanyName || record.CompanyName.trim() === '') {
        newErrors.CompanyName = "This field is required";
      } else if (record.CompanyName.length > 100) {
        newErrors.CompanyName = "Maximum 100 characters allowed";
      }
      
      if (!record.Name || record.Name.trim() === '') {
        newErrors.Name = "This field is required";
      } else if (record.Name.length > 100) {
        newErrors.Name = "Maximum 100 characters allowed";
      }
      
      if (!record.Email || record.Email.trim() === '') {
        newErrors.Email = "This field is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(record.Email)) {
        newErrors.Email = "Invalid email format";
      } else if (record.Email.length > 100) {
        newErrors.Email = "Maximum 100 characters allowed";
      }
      
      if (!record.PhoneNumber || record.PhoneNumber.trim() === '') {
        newErrors.PhoneNumber = "This field is required";
      } else if (record.PhoneNumber.length > 20) {
        newErrors.PhoneNumber = "Maximum 20 characters allowed";
      }
      
      // Street - Required
      if (!record.Street || record.Street.trim() === '') {
        newErrors.Street = "This field is required";
      } else if (record.Street.length > 100) {
        newErrors.Street = "Maximum 100 characters allowed";
      }
      
      // Box - Optional (only validate length if provided)
      if (record.Box && record.Box.length > 20) {
        newErrors.Box = "Maximum 20 characters allowed";
      }
      
      // PostCode - Required
      if (!record.PostCode || record.PostCode.trim() === '') {
        newErrors.PostCode = "This field is required";
      } else if (record.PostCode.length > 20) {
        newErrors.PostCode = "Maximum 20 characters allowed";
      }
      
      // City - Required
      if (!record.City || record.City.trim() === '') {
        newErrors.City = "This field is required";
      } else if (record.City.length > 50) {
        newErrors.City = "Maximum 50 characters allowed";
      }
      
      // Country - Required
      if (!record.Country || record.Country.trim() === '') {
        newErrors.Country = "This field is required";
      } else if (record.Country.length > 50) {
        newErrors.Country = "Maximum 50 characters allowed";
      }

      // Peppol ID - Optional with format validation
      if (record.CustomerPeppolId && record.CustomerPeppolId.trim() !== '') {
        if (record.CustomerPeppolId.length > 50) {
          newErrors.CustomerPeppolId = "Maximum 50 characters allowed";
        } else {
          const peppolPattern = /^(9925:BE\d+|0208:\d+)$/;
          if (!peppolPattern.test(record.CustomerPeppolId.trim())) {
            newErrors.CustomerPeppolId = "Invalid Peppol ID format. Use 9925:BE1234567890 or 0208:1234567890";
          } else {
            // Validate VAT number matches Peppol ID
            const cleanVAT = formattedVATNumber.replace(/^BE/i, '').trim();
            if (record.CustomerPeppolId.startsWith('9925:BE')) {
              const peppolVAT = record.CustomerPeppolId.substring(8); // Remove '9925:BE'
              if (peppolVAT !== cleanVAT) {
                newErrors.CustomerPeppolId = "Peppol ID VAT number does not match the entered VAT number";
              }
            } else if (record.CustomerPeppolId.startsWith('0208:')) {
              const peppolVAT = record.CustomerPeppolId.substring(5); // Remove '0208:'
              if (peppolVAT !== cleanVAT) {
                newErrors.CustomerPeppolId = "Peppol ID VAT number does not match the entered VAT number";
              }
            }
          }
        }
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        setLoading(false);
        
        setTimeout(() => {
          const firstErrorField = Object.keys(newErrors)[0];
          const element = document.querySelector(`[name="${firstErrorField}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.focus();
          }
        }, 100);
        
        return;
      }

      // ✅ Call appropriate API based on mode
      if (isEditMode) {
        await updateCustomer(id, record);
        toast.success(t("customer_updated_success"));
        navigate('/customers'); // ✅ Changed to redirect to customers list
      } else {
        await addCustomer(record);
        toast.success(t("customer_created_success"));
        navigate('/customers');
      }
      
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'adding'} customer:`, error);
      const errorMessage = error.response?.data?.message || '';
      
      // Display all backend errors at the bottom of the form, no toasts
      if (errorMessage) {
        setServerError(errorMessage);
        // Scroll to the error message
        setTimeout(() => {
          const errorElement = document.getElementById('server-error-message');
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // ✅ Navigate to customers list page for both add and edit
    navigate('/customers');
  };

  // ✅ Show loading spinner while fetching customer data in edit mode
  if (initialLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 1, mb: 4 }}>
      <Box sx={{ mb: 2 }}>
        {/* ✅ Dynamic header based on mode */}
        <Typography 
          variant="h5" 
          component="h1" 
          sx={{ 
            fontWeight: 600, 
            color: 'primary.main',
            textAlign: 'left', 
            mb: 0.5 
          }}
        >
          {isEditMode ? t("edit_customer") : t("add_customer")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isEditMode ? t("edit_customer_desc") : t("add_customer_desc")}
        </Typography>
      </Box>

      <Paper 
        elevation={2} 
        sx={{ 
          p: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <form onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 1.5,
                pb: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'text.primary',
                    fontSize: '1.1rem'
                  }}
                >
                  {t("basic_information")}
                </Typography>
              </Box>
            </Grid>

            {/* VAT Number */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  name="VATNumber"
                  label={t("vat_number") + " *"}
                  value={VATNumber}
                  onChange={(e) => {
                    setVATNumber(e.target.value);
                    if (errors.VATNumber) {
                      setErrors(prev => ({ ...prev, VATNumber: undefined }));
                    }
                    if (serverError) {
                      setServerError('');
                    }
                  }}
                  fullWidth
                  error={Boolean(errors.VATNumber)}
                  helperText={errors.VATNumber || t("vat_search_hint") || 'Enter VAT without BE prefix'}
                  disabled={loading}
                  size="medium"
                  inputProps={{ maxLength: 20 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>BE</Typography>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ 
                    flex: 1,
                    '& .MuiOutlinedInput-root': { 
                      borderRadius: 1.5,
                      '&.Mui-error': {
                        '& fieldset': {
                          borderColor: '#d32f2f',
                          borderWidth: '2px'
                        }
                      }
                    },
                    '& .MuiInputLabel-root': {
                      '&.Mui-error': {
                        color: 'rgba(0, 0, 0, 0.6)'
                      }
                    },
                    '& .MuiFormHelperText-root': {
                      marginLeft: 0.5,
                      fontSize: '0.75rem',
                      '&.Mui-error': {
                        color: '#d32f2f'
                      }
                    }
                  }}
                />
                {!isEditMode && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleVATLookup}
                    disabled={loading || vatSearching || !VATNumber}
                    sx={{
                      minWidth: 100,
                      height: 56,
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 500,
                      boxShadow: 2,
                      '&:hover': {
                        boxShadow: 3
                      }
                    }}
                  >
                    {vatSearching ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      t("validate") || "Validate"
                    )}
                  </Button>
                )}
              </Box>
            </Grid>

            {/* Company Name */}
            <Grid item xs={12} md={6}>
              <TextField
                name="CompanyName"
                label={t("company_name") + " *"}
                value={companyName}
                onChange={(e) => {
                  setCompanyName(e.target.value);
                  if (errors.CompanyName) {
                    setErrors(prev => ({ ...prev, CompanyName: undefined }));
                  }
                }}
                fullWidth
                error={Boolean(errors.CompanyName)}
                helperText={errors.CompanyName || ' '}
                disabled={loading}
                size="medium"
                inputProps={{ maxLength: 100 }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#d32f2f',
                        borderWidth: '2px'
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-error': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0.5,
                    fontSize: '0.75rem',
                    '&.Mui-error': {
                      color: '#d32f2f'
                    }
                  }
                }}
              />
            </Grid>

            {/* Customer Name */}
            <Grid item xs={12} md={6}>
              <TextField
                name="Name"
                label={t("customer_name") + " *"}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (errors.Name) {
                    setErrors(prev => ({ ...prev, Name: undefined }));
                  }
                }}
                fullWidth
                error={Boolean(errors.Name)}
                helperText={errors.Name || ' '}
                disabled={loading}
                size="medium"
                inputProps={{ maxLength: 100 }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#d32f2f',
                        borderWidth: '2px'
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-error': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0.5,
                    fontSize: '0.75rem',
                    '&.Mui-error': {
                      color: '#d32f2f'
                    }
                  }
                }}
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} md={6}>
              <TextField
                name="Email"
                label={t("email_address") + " *"}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.Email) {
                    setErrors(prev => ({ ...prev, Email: undefined }));
                  }
                }}
                fullWidth
                type="email"
                error={Boolean(errors.Email)}
                helperText={errors.Email || ' '}
                disabled={loading}
                size="medium"
                inputProps={{ maxLength: 100 }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#d32f2f',
                        borderWidth: '2px'
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-error': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0.5,
                    fontSize: '0.75rem',
                    '&.Mui-error': {
                      color: '#d32f2f'
                    }
                  }
                }}
              />
            </Grid>

            {/* Phone Number */}
            <Grid item xs={12} md={6}>
              <TextField
                name="PhoneNumber"
                label={t("phone_number") + " *"}
                value={phoneNumber}
                onChange={(e) => {
                  setPhoneNumber(e.target.value);
                  if (errors.PhoneNumber) {
                    setErrors(prev => ({ ...prev, PhoneNumber: undefined }));
                  }
                }}
                fullWidth
                error={Boolean(errors.PhoneNumber)}
                helperText={errors.PhoneNumber || ' '}
                disabled={loading}
                size="medium"
                inputProps={{ maxLength: 20 }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#d32f2f',
                        borderWidth: '2px'
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-error': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0.5,
                    fontSize: '0.75rem',
                    '&.Mui-error': {
                      color: '#d32f2f'
                    }
                  }
                }}
              />
            </Grid>

            {/* Peppol ID */}
            <Grid item xs={12} md={6}>
              <TextField
                name="CustomerPeppolId"
                label={t("peppol_id")}
                value={peppolId}
                onChange={(e) => {
                  setPeppolId(e.target.value);
                  if (errors.CustomerPeppolId) {
                    setErrors(prev => ({ ...prev, CustomerPeppolId: undefined }));
                  }
                }}
                fullWidth
                error={Boolean(errors.CustomerPeppolId)}
                helperText={errors.CustomerPeppolId || t("peppol_id_hint") || "Optional. Format: 9925:BE1234567890 or 0208:1234567890"}
                disabled={loading}
                size="medium"
                placeholder="9925:BE1234567890 or 0208:1234567890"
                inputProps={{ maxLength: 50 }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#d32f2f',
                        borderWidth: '2px'
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-error': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0.5,
                    fontSize: '0.75rem',
                    '&.Mui-error': {
                      color: '#d32f2f'
                    }
                  }
                }}
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                disabled={loading}
                size="medium"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              >
                <InputLabel>{t("status")}</InputLabel>
                <Select
                  value={isActive}
                  onChange={e => setIsActive(e.target.value)}
                  label={t("status")}
                >
                  <MenuItem value="true">{t("active")}</MenuItem>
                  <MenuItem value="false">{t("inactive")}</MenuItem>
                </Select>
                <FormHelperText sx={{ marginLeft: 0.5, fontSize: '0.75rem' }}>
                  {' '}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 1.5,
                mt: 2,
                pb: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'text.primary',
                    fontSize: '1.1rem'
                  }}
                >
                  {t("address_information")}
                </Typography>
              </Box>
            </Grid>

            {/* Street Address */}
            <Grid item xs={12}>
              <TextField
                name="Street"
                label={t("street_address") + " *"}
                value={street}
                onChange={(e) => {
                  setStreet(e.target.value);
                  if (errors.Street) {
                    setErrors(prev => ({ ...prev, Street: undefined }));
                  }
                }}
                fullWidth
                error={Boolean(errors.Street)}
                helperText={errors.Street || ' '}
                disabled={loading}
                multiline
                rows={2}
                size="medium"
                inputProps={{ maxLength: 100 }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#d32f2f',
                        borderWidth: '2px'
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-error': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0.5,
                    fontSize: '0.75rem',
                    '&.Mui-error': {
                      color: '#d32f2f'
                    }
                  }
                }}
              />
            </Grid>

            {/* Box */}
            <Grid item xs={12} md={4}>
              <TextField
                name="Box"
                label={t("box_unit")}
                value={box}
                onChange={(e) => {
                  setBox(e.target.value);
                  if (errors.Box) {
                    setErrors(prev => ({ ...prev, Box: undefined }));
                  }
                }}
                fullWidth
                error={Boolean(errors.Box)}
                helperText={errors.Box || ' '}
                disabled={loading}
                size="medium"
                inputProps={{ maxLength: 20 }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#d32f2f',
                        borderWidth: '2px'
                      }
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0.5,
                    fontSize: '0.75rem'
                  }
                }}
              />
            </Grid>

            {/* Post Code */}
            <Grid item xs={12} md={4}>
              <TextField
                name="PostCode"
                label={t("postal_code") + " *"}
                value={postCode}
                onChange={(e) => {
                  setPostCode(e.target.value);
                  if (errors.PostCode) {
                    setErrors(prev => ({ ...prev, PostCode: undefined }));
                  }
                }}
                fullWidth
                error={Boolean(errors.PostCode)}
                helperText={errors.PostCode || ' '}
                disabled={loading}
                size="medium"
                inputProps={{ maxLength: 20 }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#d32f2f',
                        borderWidth: '2px'
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-error': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0.5,
                    fontSize: '0.75rem',
                    '&.Mui-error': {
                      color: '#d32f2f'
                    }
                  }
                }}
              />
            </Grid>

            {/* City */}
            <Grid item xs={12} md={4}>
              <TextField
                name="City"
                label={t("city") + " *"}
                value={city}
                onChange={(e) => {
                  setCity(e.target.value);
                  if (errors.City) {
                    setErrors(prev => ({ ...prev, City: undefined }));
                  }
                }}
                fullWidth
                error={Boolean(errors.City)}
                helperText={errors.City || ' '}
                disabled={loading}
                size="medium"
                inputProps={{ maxLength: 50 }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#d32f2f',
                        borderWidth: '2px'
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-error': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0.5,
                    fontSize: '0.75rem',
                    '&.Mui-error': {
                      color: '#d32f2f'
                    }
                  }
                }}
              />
            </Grid>

            {/* Country */}
            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                error={Boolean(errors.Country)}
                disabled={loading}
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#d32f2f',
                        borderWidth: '2px'
                      }
                    }
                  }
                }}
              >
                <InputLabel>{t("country") + " *"}</InputLabel>
                <Select
                  value={country}
                  onChange={(e) => {
                    setCountry(e.target.value);
                    if (errors.Country) {
                      setErrors(prev => ({ ...prev, Country: undefined }));
                    }
                  }}
                  label={t("country")}
                >
                  {countries.map((c) => (
                    <MenuItem key={c.code} value={c.name}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText sx={{ marginLeft: 0.5, fontSize: '0.75rem' }}>
                  {errors.Country || ' '}
                </FormHelperText>
              </FormControl>
            </Grid>

            {/* Server Error Message */}
            {serverError && (
              <Grid item xs={12}>
                <Box
                  id="server-error-message"
                  sx={{
                    p: 2,
                    backgroundColor: '#ffebee',
                    borderRadius: 1,
                    border: '1px solid #ef5350',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#d32f2f',
                      fontWeight: 500,
                      flex: 1
                    }}
                  >
                    {serverError}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setServerError('')}
                    sx={{ color: '#d32f2f' }}
                  >
                    ×
                  </IconButton>
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                justifyContent: 'flex-end', 
                mt: 3,
                pt: 2,
                borderTop: '1px solid',
                borderColor: 'divider'
              }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleCancel}
                  disabled={loading}
                  sx={{ 
                    minWidth: 120,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  {t("cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  sx={{ 
                    minWidth: 120,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    boxShadow: 2
                  }}
                >
                  {loading 
                    ? (isEditMode ? t("saving") : t("creating")) 
                    : (isEditMode ? t("save_changes") : t("create_customer"))
                  }
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Company Data Confirmation Dialog */}
      <Dialog 
        open={companyDataDialog} 
        onClose={() => {
          setCompanyDataDialog(false);
          setCompanyData(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 3
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 600, 
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 2
        }}>
          {t("confirm_company_data") || "Confirm Company Data"}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {companyData && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("company_data_found") || "The following company information was found. Please confirm to populate the form."}
              </Typography>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: '40%' }}>
                      {t("enterprise_number") || "Enterprise Number"}
                    </TableCell>
                    <TableCell>{companyData.EnterpriseNumber || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("company_name") || "Company Name"}
                    </TableCell>
                    <TableCell>{companyData.CompanyName || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("street_address") || "Street Address"}
                    </TableCell>
                    <TableCell>{companyData.StreetAddress || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("box_unit") || "Box/Unit"}
                    </TableCell>
                    <TableCell>{companyData.Box || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("postal_code") || "Postal Code"}
                    </TableCell>
                    <TableCell>{companyData.PostCode || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("city") || "City"}
                    </TableCell>
                    <TableCell>{companyData.City || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("status") || "Status"}
                    </TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>
                      {companyData.Status || '-'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("start_date") || "Start Date"}
                    </TableCell>
                    <TableCell>{companyData.StartDate || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("email_address") || "Email"}
                    </TableCell>
                    <TableCell>{companyData.Email || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("phone_number") || "Phone"}
                    </TableCell>
                    <TableCell>{companyData.Phone || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("website") || "Website"}
                    </TableCell>
                    <TableCell>{companyData.Website || '-'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          px: 3, 
          py: 2, 
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
          <Button 
            onClick={() => {
              setCompanyDataDialog(false);
              setCompanyData(null);
            }}
            variant="outlined"
            color="secondary"
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            {t("cancel") || "Cancel"}
          </Button>
          <Button 
            onClick={handleConfirmCompanyData}
            variant="contained"
            color="primary"
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              boxShadow: 2
            }}
          >
            {t("confirm_use_data") || "Confirm & Use Data"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AddCustomerPage;